import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {MessageService, PrimeTemplate} from 'primeng/api';
import {Button} from 'primeng/button';
import {InputText} from 'primeng/inputtext';

import {Divider} from 'primeng/divider';
import {Select, SelectChangeEvent} from 'primeng/select';
import {InputMask} from 'primeng/inputmask';
import {InputNumber} from 'primeng/inputnumber';
import {Fluid} from 'primeng/fluid';
import {FileSelectEvent, FileUpload} from 'primeng/fileupload';
import {Tooltip} from 'primeng/tooltip';
import {TipoRepresentacao} from '@core/models/tipo-representacao.enum';
import {RepresentacaoGovbrCadastroPayload, TipoRepresentacaoService} from '@core/services/tipo-representacao.service';
import {EnumResponse} from '@core/models/enum-response';
import {StringUtils} from '@shared/utils/string.utils';
import {
    IconBoldDocumentDownloadComponent
} from '@components/icons/icon-bold-document-download/icon-bold-document-download.component';
import {IconBoldTrash} from '@components/icons/icon-bold-trash/icon-bold-trash';
import {
    IconBoldDocumentUploadComponent
} from '@components/icons/icon-bold-document-upload/icon-bold-document-upload.component';
import {
    ID_REPRESENTACAO_KEY,
    ID_TIPO_REPRESENTACAO_GOVBR_KEY,
    PODE_ACAO_DEMANDA_KEY,
    TIPO_REPRESENTACAO_GOVBR_KEY
} from '@shared/constants/storage-keys';
import {MSG_REQUIRED_FIELDS_FORM} from '@shared/constants/messages';

@Component({
    selector: 'app-form-representacao-govbr',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        InputText,
        Divider,
        Select,
        InputMask,
        InputNumber,
        Fluid,
        FileUpload,
        PrimeTemplate,
        IconBoldDocumentDownloadComponent,
        IconBoldTrash,
        Tooltip,
        Button,
        IconBoldDocumentUploadComponent
    ],
    templateUrl: './form-representacao-govbr.component.html',
    styleUrl: './form-representacao-govbr.component.css',
})
export class FormRepresentacaoGovbrComponent implements OnInit {
    @Input() mode: 'cadastro' | 'reforcar' = 'cadastro';

    form!: FormGroup;
    showLoadingSaveButton = false;
    showForm = false;
    tipoRepresentacaoOptions: { label: string; value: EnumResponse }[] = [];
    labelFormControlNome = 'Nome da Entidade';
    labelFormControlArquivo = 'Arquivo';
    isRequiredFormControlArquivo = false;
    file: File | null = null;
    fileTooLarge = false;
    fileInvalidExtension = false;

    protected readonly TipoRepresentacao = TipoRepresentacao;

    private readonly FILE_MAX_BYTES = 10 * 1024 * 1024;
    private readonly FILE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'bmp'];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly service: TipoRepresentacaoService,
    ) {
    }

    get isCidadaoSelecionado(): boolean {
        const tipo = this.form?.get('tipoRepresentacao')?.getRawValue() as EnumResponse | null;
        return tipo?.nome === TipoRepresentacao.CIDADAO;
    }

    get tipoRepresentacaoSelecionado(): EnumResponse | null {
        return this.form.get('tipoRepresentacao')?.getRawValue();
    }

    ngOnInit(): void {
        this.initForm();
        this.getTipoRepresentacaoOptions();
    }

    getTipoRepresentacaoOptions(): void {
        this.service.getEnums().subscribe({
            next: result => {
                this.tipoRepresentacaoOptions = result
                    .filter(item => !item.nome.toUpperCase().includes('ARTESP'))
                    .map(item => ({label: item.nome, value: item}));
            },
        });
    }

    initForm(): void {
        this.form = this.formBuilder.group({
            tipoRepresentacao: [null, Validators.required],
            email: [null, [Validators.required, Validators.email]],
            nomeOrganizacao: [null, Validators.required],
            cnpj: [null, Validators.required],
            cargo: [null, Validators.required],
            cep: [null, Validators.required],
            logradouro: [{value: '', disabled: false}, Validators.required],
            numero: [null, Validators.required],
            complemento: [null],
            bairro: [{value: null, disabled: false}, Validators.required],
            municipio: [{value: null, disabled: false}, Validators.required],
            uf: [{value: null, disabled: false}, Validators.required],
            arquivo: [null],
        });

        this.form.get('tipoRepresentacao')?.valueChanges.subscribe((value: EnumResponse) => {
            this.form.markAsUntouched();
            const cnpjControl = this.form.get('cnpj');
            const arquivoControl = this.form.get('arquivo');
            if (!cnpjControl || !arquivoControl) return;

            switch (value.nome) {
                case TipoRepresentacao.SCO:
                    this.labelFormControlNome = 'Nome da Organização';
                    this.labelFormControlArquivo = 'Ato Constitutivo da SCO';
                    cnpjControl.setValidators([Validators.required]);
                    arquivoControl.setValidators([Validators.required]);
                    this.isRequiredFormControlArquivo = true;
                    break;

                case TipoRepresentacao.AGENTE_PUBLICO_LEGISLATIVO:
                case TipoRepresentacao.AGENTE_PUBLICO_EXECUTIVO:
                    this.labelFormControlNome = 'Nome da Entidade';
                    this.labelFormControlArquivo = 'Documento de Nomeação';
                    arquivoControl.setValidators([Validators.required]);
                    this.isRequiredFormControlArquivo = true;
                    cnpjControl.clearValidators();
                    break;

                default:
                    this.labelFormControlNome = 'Nome da Entidade';
                    this.labelFormControlArquivo = 'Documento de Nomeação';
                    cnpjControl.clearValidators();
                    arquivoControl.clearValidators();
                    this.isRequiredFormControlArquivo = false;
                    break;
            }
            cnpjControl.updateValueAndValidity();
            arquivoControl.updateValueAndValidity();
        });
    }

    onChangeTipoRepresentacao(event: SelectChangeEvent): void {
        const tipo = event.value as EnumResponse;

        if (tipo.nome === TipoRepresentacao.CIDADAO) {
            this.showForm = false;
            for (const key of Object.keys(this.form.controls)) {
                const control = this.form.get(key);
                control?.clearValidators();
                control?.updateValueAndValidity();
            }

            // No modo cadastro, redireciona automaticamente para adicionar demanda
            if (this.mode === 'cadastro') {
                this.form.get('tipoRepresentacao')?.disable();
                this.showLoadingSaveButton = true;
                setTimeout(() => {
                    this.setStorageRepresentacao();
                    this.router.navigate(['/demandas/adicionar']);
                }, 3000);
            }
        } else {
            setTimeout(() => {
                this.showForm = true;
            });
        }
    }

    /** Usado no modo cadastro: valida e submete o formulário, redirecionando ao final. */
    submit(): void {
        if (this.form.invalid) {
            this.messageService.add({severity: 'warn', summary: 'Atenção!', detail: MSG_REQUIRED_FIELDS_FORM});
            this.form.markAllAsTouched();
            return;
        }
        this.showLoadingSaveButton = true;
        this.service.save(this.buildFormData()).subscribe({
            next: (response: RepresentacaoGovbrCadastroPayload) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso!',
                    detail: 'Representação cadastrada com sucesso.',
                });
                setTimeout(() => {
                    if (response?.id) {
                        this.setStorageRepresentacao(response.id);
                    }
                    this.router.navigate(['/demandas/adicionar']).then(() => {
                        this.showLoadingSaveButton = false;
                    });
                }, 1000);
            },
            error: (error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro!',
                    detail: error?.error?.message || error?.message || 'Falha ao salvar tipo de representação. Tente novamente mais tarde',
                });
                this.showLoadingSaveButton = false;
            },
            complete: () => {
                this.showForm = false;
                this.showLoadingSaveButton = false;
            },
        });
    }

    /** Retorna o FormData montado a partir do formulário. Usado pelo componente pai no modo reforcar. */
    buildFormData(): FormData {
        const formValues = this.form.getRawValue();
        const tipo = formValues.tipoRepresentacao as EnumResponse;
        const formData = new FormData();
        formData.append('tipoRepresentacao', tipo?.nome ?? '');
        formData.append('idTipoRepresentacao', String(tipo?.id ?? ''));
        formData.append('email', formValues.email ?? '');
        formData.append('nomeOrganizacao', formValues.nomeOrganizacao ?? '');
        formData.append('cnpj', this.removeMask(formValues.cnpj ?? ''));
        formData.append('cargo', formValues.cargo ?? '');
        formData.append('cep', this.removeMask(formValues.cep ?? ''));
        formData.append('logradouro', formValues.logradouro ?? '');
        formData.append('numero', String(formValues.numero ?? ''));
        formData.append('complemento', formValues.complemento ?? '');
        formData.append('bairro', formValues.bairro ?? '');
        formData.append('municipio', formValues.municipio ?? '');
        formData.append('uf', formValues.uf ?? '');
        if (this.file) {
            formData.append('arquivo', this.file, this.file.name);
        }
        return formData;
    }

    /** Persiste os dados da representação na sessão. Chamado externamente no modo reforcar após receber o ID. */
    setStorageRepresentacao(idRepresentacao?: number): void {
        const tipo = this.form.get('tipoRepresentacao')?.getRawValue() as EnumResponse;
        if (tipo?.id) {
            sessionStorage.setItem(TIPO_REPRESENTACAO_GOVBR_KEY, tipo.nome);
            sessionStorage.setItem(ID_TIPO_REPRESENTACAO_GOVBR_KEY, JSON.stringify(tipo.id));
        }
        if (idRepresentacao) {
            sessionStorage.setItem(ID_REPRESENTACAO_KEY, JSON.stringify(idRepresentacao));
        }
        sessionStorage.setItem(PODE_ACAO_DEMANDA_KEY, 'true');
    }

    protected onSelectArquivo(event: FileSelectEvent): void {
        this.fileTooLarge = false;
        this.fileInvalidExtension = false;
        const file = event.currentFiles[0];
        if (!file) return;
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        if (!this.FILE_EXTENSIONS.includes(ext)) {
            this.fileInvalidExtension = true;
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção!',
                detail: `Formato inválido. Formatos permitidos: ${this.FILE_EXTENSIONS.map(e => e.toUpperCase()).join(', ')}`,
            });
            return;
        }
        if (file.size > this.FILE_MAX_BYTES) {
            this.fileTooLarge = true;
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção!',
                detail: `Tamanho do arquivo não permitido. Somente arquivos até ${this.getFormattedFileSize()}.`,
            });
            return;
        }
        this.file = file;
        this.form.get('arquivo')?.setValue(file.name);
    }

    protected onRemoveFile(): void {
        this.file = null;
        this.form.get('arquivo')?.setValue('');
    }

    protected getFormattedFileSize(): string {
        return StringUtils.getFormattedFileSize(this.FILE_MAX_BYTES);
    }

    private removeMask(value: string): string {
        return value.replaceAll(/\D/g, '');
    }
}
